import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format, addDays, subDays, isSameDay } from 'date-fns';
import {
  Phone, ClipboardList, DollarSign, Presentation, Users, Headphones,
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, CheckCircle2, RotateCcw,
  Plus, Minus, Loader2, Unlock, TrendingUp, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/dashboard/PageHeader';
import LeadRoiReport from '../components/LeadRoiReport';
import { TrackerLeaderboard } from '../components/TrackerLeaderboard';
import TrackerHeatmap from '../components/TrackerHeatmap';
import TrackerWeeklyGrid from '../components/TrackerWeeklyGrid';
import AgentBreakdown from '../components/AgentBreakdown';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { TrackerRecapModal } from '../components/tracker/TrackerRecapModal';
import { Share2, UserCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MILESTONES = [25, 50, 75, 100, 125, 150, 175, 200, 225, 250, 275, 300, 325, 350, 375, 400, 425, 450, 475, 500];

const fmtDate = (d) => format(d, 'EEE, MMM d, yyyy');
const isoDate = (d) => format(d, 'yyyy-MM-dd');

// ---------- Reusable counter row ----------
const CounterRow = ({ icon: Icon, accent, label, sublabel, value, onPlus, onMinus, locked, testId }) => (
  <div
    className="flex items-center gap-3 rounded-2xl border border-[#EDF1FA] bg-white p-3 sm:gap-4 sm:p-4 transition hover:border-[#D9E1F2]"
    data-testid={testId}
  >
    <div className={`flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl ${accent}`}>
      <Icon className="h-5 w-5 text-white" strokeWidth={2} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] sm:text-sm font-semibold text-[#2B3674] leading-tight break-words">{label}</div>
      {sublabel ? <div className="text-[10px] sm:text-[11px] text-[#A3AED0] leading-tight mt-0.5">{sublabel}</div> : null}
    </div>
    <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
      <button
        type="button"
        onClick={onMinus}
        disabled={locked}
        className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-[#F4F7FE] text-[#2B3674] transition hover:bg-[#E9EDF7] disabled:cursor-not-allowed disabled:opacity-40"
        data-testid={`${testId}-minus`}
      >
        <Minus className="h-4 w-4" />
      </button>
      <div className="min-w-[2ch] sm:min-w-[3ch] text-center font-['Manrope'] text-xl sm:text-2xl font-extrabold text-[#2B3674] tabular-nums" data-testid={`${testId}-value`}>
        {value}
      </div>
      <button
        type="button"
        onClick={onPlus}
        disabled={locked}
        className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-[#2B3674] text-white transition hover:bg-[#1f284f] disabled:cursor-not-allowed disabled:opacity-40"
        data-testid={`${testId}-plus`}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  </div>
);

export default function Tracker() {
  const { getAuthHeader } = useAuth();
  const [date, setDate] = useState(new Date());
  const [period, setPeriod] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [roiOpen, setRoiOpen] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);

  // Counters bound to the SELECTED DAY
  const [dials, setDials] = useState(0);
  const [contacts, setContacts] = useState(0);
  const [appointments, setAppointments] = useState(0);
  const [showed, setShowed] = useState(0);
  const [presentations, setPresentations] = useState(0);
  const [sales, setSales] = useState(0);
  const [recruits, setRecruits] = useState(0);
  const [locked, setLocked] = useState(false);

  // Period totals drive the funnel insights
  const [periodTotals, setPeriodTotals] = useState({
    dials_made: 0, contacts_made: 0, appointments_set: 0,
    presentations_given: 0, sales_made: 0, recruits_onboarded: 0,
  });

  // Compare chart state
  const [leaderboardTick, setLeaderboardTick] = useState(0);

  const isoDay = isoDate(date);
  const isToday = isSameDay(date, new Date());

  // ---------- Fetch the selected day's entry ----------
  const fetchDailyEntry = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kpi`, {
        ...getAuthHeader(),
        params: { start_date: isoDay, end_date: isoDay },
      });
      const today = res.data.find(r => r.date === isoDay);
      if (today) {
        setDials(today.dials_made || 0);
        setContacts(today.contacts_made || 0);
        setAppointments(today.appointments_set || 0);
        setShowed(today.appointments_showed || 0);
        setPresentations(today.presentations_given || 0);
        setSales(today.sales_made || 0);
        setRecruits(today.recruits_onboarded || 0);
        setLocked(!!today.locked);
      } else {
        setDials(0); setContacts(0); setAppointments(0); setShowed(0);
        setPresentations(0); setSales(0); setRecruits(0);
        setLocked(false);
      }
    } catch (e) {
      console.error('Failed to load entry', e);
    }
  }, [isoDay, getAuthHeader]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tracker/summary`, {
        ...getAuthHeader(),
        params: { date: isoDay, period },
      });
      setPeriodTotals({
        dials_made: res.data.dials_made || 0,
        contacts_made: res.data.contacts_made || 0,
        appointments_set: res.data.appointments_set || 0,
        presentations_given: res.data.presentations_given || 0,
        sales_made: res.data.sales_made || 0,
        recruits_onboarded: res.data.recruits_onboarded || 0,
      });
    } catch (e) {
      console.error('Summary fetch failed', e);
    }
  }, [isoDay, period, getAuthHeader]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchDailyEntry(), fetchSummary()]).finally(() => setLoading(false));
  }, [fetchDailyEntry, fetchSummary]);

  // ---------- Midnight auto-rollover ----------
  // When the local calendar day flips, automatically jump the view to the new
  // day so counters reset to 0. The previous day's totals are already persisted
  // by the debounced auto-save below (data is keyed by date in the DB).
  useEffect(() => {
    if (!isToday) return; // only roll over if user is currently viewing today
    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5);
    const msUntilMidnight = nextMidnight.getTime() - now.getTime();
    const t = setTimeout(() => {
      setDate(new Date());
      toast.success('New day — tracker reset for today');
    }, msUntilMidnight);
    return () => clearTimeout(t);
  }, [isToday, isoDay]);

  // ---------- Persist (debounced) ----------
  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(async () => {
      try {
        setSaving(true);
        await axios.post(`${API}/kpi`, {
          date: isoDay,
          dials_made: dials,
          contacts_made: contacts,
          appointments_set: appointments,
          appointments_showed: showed,
          presentations_given: presentations,
          sales_made: sales,
          recruits_onboarded: recruits,
          minutes_worked: 0,  // kept for back-compat
          locked,
        }, getAuthHeader());
        fetchSummary();
        setLeaderboardTick((t) => t + 1);
      } catch (e) {
        toast.error('Save failed');
      } finally {
        setSaving(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [dials, contacts, appointments, showed, presentations, sales, recruits]);

  // ---------- Derived ----------
  const contactRate = useMemo(() => {
    if (!periodTotals.dials_made) return 0;
    return (periodTotals.contacts_made / periodTotals.dials_made) * 100;
  }, [periodTotals]);

  const presentationRate = useMemo(() => {
    if (!periodTotals.contacts_made) return 0;
    return (periodTotals.presentations_given / periodTotals.contacts_made) * 100;
  }, [periodTotals]);

  const closeRate = useMemo(() => {
    if (!periodTotals.presentations_given) return 0;
    return (periodTotals.sales_made / periodTotals.presentations_given) * 100;
  }, [periodTotals]);

  const nextMilestone = useMemo(
    () => MILESTONES.find(m => m > dials) || MILESTONES[MILESTONES.length - 1],
    [dials]
  );
  const milestoneProgress = useMemo(() => {
    const prev = [...MILESTONES].reverse().find(m => m <= dials) || 0;
    return Math.min(100, ((dials - prev) / (nextMilestone - prev)) * 100);
  }, [dials, nextMilestone]);

  const bump = (setter, val) => (delta) => {
    if (locked) {
      toast.error('Today is confirmed. Unlock to edit.');
      return;
    }
    setter(Math.max(0, val + delta));
  };

  const handleConfirm = async () => {
    try {
      await axios.post(`${API}/tracker/daily/lock`,
        { date: isoDay, locked: !locked },
        getAuthHeader()
      );
      setLocked(!locked);
      toast.success(!locked ? 'Activity confirmed for today' : 'Today unlocked');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleReset = async () => {
    if (locked) {
      toast.error('Day is confirmed. Unlock first.');
      return;
    }
    if (!window.confirm('Reset all counters for this day to 0?')) return;
    try {
      await axios.post(`${API}/tracker/daily/reset`, { date: isoDay }, getAuthHeader());
      setDials(0); setContacts(0); setAppointments(0); setShowed(0);
      setPresentations(0); setSales(0); setRecruits(0);
      fetchSummary();
      toast.success('Day reset');
    } catch (e) {
      toast.error('Failed to reset');
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="tracker-page">
      <PageHeader
        title="Tracker"
        subtitle={`Log your hustle • ${isToday ? 'Today' : fmtDate(date)}`}
        actions={
          <div className="flex items-center gap-3">
            {saving ? (
              <span className="flex items-center gap-1.5 text-xs text-[#A3AED0]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => setRecapOpen(true)}
              className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#23ACD5] to-[#00E5FF] px-4 py-2 font-['Manrope'] text-xs font-extrabold text-[#06263A] shadow-[0_8px_24px_rgba(35,172,213,0.40)] transition-transform hover:scale-[1.02]"
              data-testid="tracker-share-recap-btn"
            >
              <Share2 className="h-4 w-4" /> Share recap
            </button>
          </div>
        }
      />
      <TrackerRecapModal open={recapOpen} onOpenChange={setRecapOpen} />

      {/* Period + date controls */}
      <Card className="bg-white">
        <CardContent className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center rounded-lg bg-[#F4F7FE] p-1">
              {[
                { k: 'daily', label: 'Daily' },
                { k: 'weekly', label: 'This Week' },
                { k: 'monthly', label: 'This Month' },
              ].map(p => (
                <button
                  key={p.k}
                  type="button"
                  onClick={() => setPeriod(p.k)}
                  data-testid={`period-${p.k}`}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                    period === p.k
                      ? 'bg-[#2B3674] text-white shadow-sm'
                      : 'text-[#2B3674] hover:bg-white/60'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setDate(subDays(date, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-[#F4F7FE]"
              data-testid="date-prev"
            >
              <ChevronLeft className="h-4 w-4 text-[#2B3674]" />
            </button>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md bg-[#F4F7FE] px-3 py-1.5 transition hover:bg-[#E9EDF7]"
                  data-testid="tracker-date-picker"
                >
                  <CalendarIcon className="h-3.5 w-3.5 text-[#A3AED0]" />
                  {isToday ? (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-[#A3AED0]">Today</span>
                  ) : null}
                  <span className="text-sm font-semibold text-[#2B3674] whitespace-nowrap">
                    {fmtDate(date)}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => { if (d) { setDate(d); setDatePickerOpen(false); } }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <button
              type="button"
              onClick={() => setDate(addDays(date, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-[#F4F7FE]"
              data-testid="date-next"
            >
              <ChevronRight className="h-4 w-4 text-[#2B3674]" />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Funnel insights */}
      <Card className="bg-white">
        <CardContent className="space-y-5 p-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#A3AED0]">Funnel — {period === 'daily' ? 'Today' : period === 'weekly' ? 'This week' : 'This month'}</div>
            <div className="text-[11px] text-[#A3AED0] mt-0.5">From the first dial to the closed sale.</div>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Contact Rate', value: contactRate, sub: `${periodTotals.contacts_made}/${periodTotals.dials_made} dials → contacts`, color: '#0EA5E9' },
              { label: 'Presentation Rate', value: presentationRate, sub: `${periodTotals.presentations_given}/${periodTotals.contacts_made} contacts → presentations`, color: '#F97316' },
              { label: 'Close Rate', value: closeRate, sub: `${periodTotals.sales_made}/${periodTotals.presentations_given} presentations → sales`, color: '#01B574' },
            ].map(r => (
              <div key={r.label}>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-semibold text-[#2B3674]">{r.label}</span>
                  <span className="font-['Manrope'] text-xl font-extrabold text-[#2B3674]" data-testid={`${r.label.toLowerCase().replace(' ','-')}-value`}>
                    {r.value.toFixed(1)}%
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#F4F7FE]">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: r.color }}
                    initial={false}
                    animate={{ width: `${Math.min(100, r.value)}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
                <div className="mt-1 text-[10px] text-[#A3AED0]">{r.sub}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Big dial counter (highlighted because dials drive everything) */}
      <Card className="bg-white">
        <CardContent className="p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#4318FF] to-[#868CFF]">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#2B3674]">Dials</div>
                <div className="text-xs text-[#A3AED0]">Next target: {nextMilestone}</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => bump(setDials, dials)(-1)}
                disabled={locked}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F4F7FE] text-[#2B3674] transition hover:bg-[#E9EDF7] disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="dials-minus"
              >
                <Minus className="h-5 w-5" />
              </button>
              <div className="min-w-[3.5ch] text-center font-['Manrope'] text-4xl font-extrabold text-[#2B3674] tabular-nums" data-testid="dials-value">
                {dials}
              </div>
              <button
                type="button"
                onClick={() => bump(setDials, dials)(1)}
                disabled={locked}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2B3674] text-white transition hover:bg-[#1f284f] disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="dials-plus"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              {[5, 10, 25].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => bump(setDials, dials)(n)}
                  disabled={locked}
                  data-testid={`dials-quick-${n}`}
                  className="rounded-md bg-[#F4F7FE] px-3 py-1.5 text-xs font-bold text-[#2B3674] transition hover:bg-[#E9EDF7] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  +{n}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[#E9EDF7]">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-[#4318FF] to-[#868CFF]"
              initial={false}
              animate={{ width: `${milestoneProgress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Milestone chips — horizontal scroll on mobile */}
          <div className="mt-4 flex gap-1.5 overflow-x-auto pb-1 sm:flex-wrap sm:gap-2 sm:overflow-visible">
            {MILESTONES.map(m => {
              const hit = dials >= m;
              return (
                <div
                  key={m}
                  data-testid={`milestone-${m}`}
                  className={`min-w-[44px] shrink-0 rounded-lg px-2 py-1.5 text-center text-xs font-bold transition ${
                    hit ? 'bg-[#01B574] text-white shadow-sm' : 'bg-[#F4F7FE] text-[#A3AED0]'
                  }`}
                >
                  {m}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Remaining counters — stacked rows, mobile-friendly */}
      <Card className="bg-white">
        <CardContent className="space-y-3 p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-[#A3AED0] px-1">
            Everything else today
          </div>
          <CounterRow icon={Headphones} accent="bg-gradient-to-br from-[#0EA5E9] to-[#38BDF8]"
                      label="Contacts" sublabel="Convo 20+ secs"
                      value={contacts}
                      onPlus={() => bump(setContacts, contacts)(1)}
                      onMinus={() => bump(setContacts, contacts)(-1)}
                      locked={locked} testId="counter-contacts" />
          <CounterRow icon={ClipboardList} accent="bg-gradient-to-br from-[#FF9000] to-[#FFB74D]"
                      label="Appointments Booked"
                      value={appointments}
                      onPlus={() => bump(setAppointments, appointments)(1)}
                      onMinus={() => bump(setAppointments, appointments)(-1)}
                      locked={locked} testId="counter-appts" />
          <CounterRow icon={UserCheck} accent="bg-gradient-to-br from-[#23ACD5] to-[#00E5FF]"
                      label="Appointments Showed" sublabel="Booked appts that actually sat"
                      value={showed}
                      onPlus={() => bump(setShowed, showed)(1)}
                      onMinus={() => bump(setShowed, showed)(-1)}
                      locked={locked} testId="counter-showed" />
          <CounterRow icon={Presentation} accent="bg-gradient-to-br from-[#F97316] to-[#FB923C]"
                      label="Presentations Given"
                      value={presentations}
                      onPlus={() => bump(setPresentations, presentations)(1)}
                      onMinus={() => bump(setPresentations, presentations)(-1)}
                      locked={locked} testId="counter-presentations" />
          <CounterRow icon={DollarSign} accent="bg-gradient-to-br from-[#01B574] to-[#43E97B]"
                      label="Sales Made"
                      value={sales}
                      onPlus={() => bump(setSales, sales)(1)}
                      onMinus={() => bump(setSales, sales)(-1)}
                      locked={locked} testId="counter-sales" />
          <CounterRow icon={Users} accent="bg-gradient-to-br from-[#7000FF] to-[#B266FF]"
                      label="Recruits Onboarded"
                      value={recruits}
                      onPlus={() => bump(setRecruits, recruits)(1)}
                      onMinus={() => bump(setRecruits, recruits)(-1)}
                      locked={locked} testId="counter-recruits" />
        </CardContent>
      </Card>

      {/* Confirm + Reset */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Button
          onClick={handleConfirm}
          className={`h-12 gap-2 text-sm font-bold ${
            locked
              ? 'bg-white border border-[#E9EDF7] text-[#2B3674] hover:bg-[#F4F7FE]'
              : 'bg-gradient-to-r from-[#01B574] to-[#43E97B] text-white shadow-[0px_12px_28px_rgba(1,181,116,0.25)] hover:opacity-95'
          }`}
          data-testid="confirm-day"
        >
          {locked ? <Unlock className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {locked ? 'Unlock to Edit' : "Confirm Today's Activity"}
        </Button>
        <Button variant="outline" onClick={handleReset} className="h-12 gap-2 text-sm font-semibold" data-testid="reset-day">
          <RotateCcw className="h-4 w-4" /> Reset Day
        </Button>
      </div>

      {/* Agency-wide leaderboard */}
      <TrackerLeaderboard date={isoDay} refreshKey={leaderboardTick} />

      {/* Activity heatmap grid */}
      <TrackerHeatmap />

      {/* Activity Grid 2 — week-over-week per month */}
      <TrackerWeeklyGrid />

      {/* Agent Breakdown — head-to-head comparison (admin / uplines only) */}
      <AgentBreakdown />

      {/* Lead ROI Report — behind a tile that opens a modal */}
      <button
        type="button"
        onClick={() => setRoiOpen(true)}
        data-testid="open-lead-roi"
        className="group relative w-full overflow-hidden rounded-2xl border border-[#EDF1FA] bg-gradient-to-r from-white via-[#F7F9FF] to-[#EEF1FF] p-5 text-left transition hover:border-[#C7D2FE] hover:shadow-[0_10px_28px_rgba(67,24,255,0.10)]"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#4318FF] to-[#868CFF] shadow-[0_8px_20px_rgba(67,24,255,0.30)]">
            <TrendingUp className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#4318FF]">Report</div>
            <div className="text-base font-extrabold text-[#2B3674]">Lead ROI Report</div>
            <div className="text-xs text-[#A3AED0]">Track spend, closed premium and ROI by lead source.</div>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-xs font-bold text-[#2B3674] group-hover:text-[#4318FF]">
            Open <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </div>
        </div>
      </button>

      <Dialog open={roiOpen} onOpenChange={setRoiOpen}>
        <DialogContent
          className="max-w-5xl max-h-[90vh] overflow-y-auto p-0"
          data-testid="lead-roi-modal"
        >
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="flex items-center gap-2 text-[#2B3674]">
              <TrendingUp className="h-5 w-5 text-[#4318FF]" /> Lead ROI Report
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            <LeadRoiReport />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

